import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import Dropdown from "../../common/dropDown";
import {
  fetchPlannerGroupsAndVisits,
  fetchPlannerStudyBundle,
} from "./eplannerService";
import {
  mapGroupOptions,
  mapLocationOptions,
  mapStudyOptions,
  mapVisitOptions,
} from "./eplannerReportUtils";
import "./eplanner.scss";

const EplannerFilterContext = createContext(null);

const EPLANNER_FILTERS_FALLBACK = {
  userData: null,
  bundle: { studies: [], locations: [], groups: [], visits: [] },
  studyIds: [],
  setStudyIds: () => {},
  locationIds: [],
  setLocationIds: () => {},
  groupId: "",
  setGroupId: () => {},
  visitId: "",
  setVisitId: () => {},
  studyOptions: [],
  locationOptions: [],
  groupOptions: [],
  visitOptions: [],
  loadingBundle: false,
};

/** Safe outside EplannerLayout (e.g. Book Appointment opened from Participant Profile). */
export function useEplannerFilters() {
  const ctx = useContext(EplannerFilterContext);
  return ctx || EPLANNER_FILTERS_FALLBACK;
}

export default function EplannerLayout({
  userData,
  children,
  extraFilters = null,
  showSectionLabel = false,
  hideFilterBar = false,
  pageClassName = "",
}) {
  const [bundle, setBundle] = useState({
    studies: [],
    locations: [],
    groups: [],
    visits: [],
  });
  const [studyIds, setStudyIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [visitId, setVisitId] = useState("");
  const [loadingBundle, setLoadingBundle] = useState(false);

  useEffect(() => {
    if (!userData?.OrganizationId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingBundle(true);
      try {
        const next = await fetchPlannerStudyBundle(userData);
        if (!cancelled) {
          setBundle((current) => ({
            ...current,
            studies: next.studies,
            locations: next.locations,
            groups: next.groups,
            visits: [],
          }));
        }
      } catch {
        if (!cancelled) {
          setBundle({ studies: [], locations: [], groups: [], visits: [] });
        }
      } finally {
        if (!cancelled) setLoadingBundle(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.OrganizationId, userData?.StaffId]);

  const studyOptions = useMemo(
    () => mapStudyOptions(bundle.studies),
    [bundle.studies],
  );

  const studyScheduleIdsForCascade = useMemo(() => {
    const allIds = studyOptions.map((option) => option.value).filter(Boolean);
    if (!studyIds.length) return allIds;
    const allowed = new Set(studyIds.map((id) => String(id).toUpperCase()));
    return allIds.filter((id) => allowed.has(String(id).toUpperCase()));
  }, [studyOptions, studyIds]);

  const studyCascadeKey = useMemo(
    () => [...studyScheduleIdsForCascade].map(String).sort().join("|"),
    [studyScheduleIdsForCascade],
  );

  useEffect(() => {
    if (!userData?.OrganizationId || !studyCascadeKey) {
      setBundle((current) => ({ ...current, groups: [], visits: [] }));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchPlannerGroupsAndVisits(
          userData,
          studyScheduleIdsForCascade,
        );
        if (!cancelled) {
          setBundle((current) => ({
            ...current,
            groups: next.groups,
            visits: next.visits,
          }));
        }
      } catch {
        if (!cancelled) {
          setBundle((current) => ({ ...current, groups: [], visits: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData, studyCascadeKey, studyScheduleIdsForCascade]);

  const locationOptions = useMemo(
    () => mapLocationOptions(bundle.locations),
    [bundle.locations],
  );
  const groupOptions = useMemo(
    () => mapGroupOptions(bundle.groups, studyIds, studyOptions),
    [bundle.groups, studyIds, studyOptions],
  );
  const visitOptions = useMemo(
    () =>
      mapVisitOptions(bundle.visits, groupId ? [groupId] : [], {
        studyOptions,
        groupOptions,
      }),
    [bundle.visits, groupId, studyOptions, groupOptions],
  );

  const value = useMemo(
    () => ({
      userData,
      bundle,
      studyIds,
      setStudyIds,
      locationIds,
      setLocationIds,
      groupId,
      setGroupId,
      visitId,
      setVisitId,
      studyOptions,
      locationOptions,
      groupOptions,
      visitOptions,
      loadingBundle,
    }),
    [
      bundle,
      groupId,
      groupOptions,
      loadingBundle,
      locationIds,
      locationOptions,
      studyIds,
      studyOptions,
      userData,
      visitId,
      visitOptions,
    ],
  );

  const rootClass = [
    "eplanner-page",
    "eplanner-list-page",
    "ctms-list-page",
    pageClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <EplannerFilterContext.Provider value={value}>
      <div className={rootClass}>
        {!hideFilterBar ? (
        <section className="eplanner-section eplanner-section--filters">
          {showSectionLabel ? (
            <div className="eplanner-section__header eplanner-section__header--filters">
              <h2 className="eplanner-section__title">Filters</h2>
            </div>
          ) : null}
          <div className="ctms-list-page__filters eplanner-layout__filters">
          <div className="ctms-list-page__filter-field eplanner-layout__filter-field">
            <label className="form-label small mb-1">Studies</label>
            <BadgeMultiSelect
              options={studyOptions}
              value={studyIds}
              onChange={setStudyIds}
              emptyLabel={loadingBundle ? "Loading…" : "All studies"}
              compactSummary
              showSearch
              showSelectAll
              popoverTitle="Studies"
              popoverSubtitle="Select one or more studies"
            />
          </div>
          <div className="ctms-list-page__filter-field eplanner-layout__filter-field">
            <label className="form-label small mb-1">Locations</label>
            <BadgeMultiSelect
              options={locationOptions}
              value={locationIds}
              onChange={setLocationIds}
              emptyLabel={loadingBundle ? "Loading…" : "All locations"}
              compactSummary
              showSearch
              showSelectAll
              popoverTitle="Locations"
              popoverSubtitle="Select one or more locations"
            />
          </div>
          <div className="ctms-list-page__filter-field eplanner-layout__filter-field">
            <label className="form-label small mb-1">Group</label>
            <Dropdown
              options={[{ value: "", label: "All groups" }, ...groupOptions]}
              selected={
                groupOptions.find((option) => option.value === groupId) || {
                  value: "",
                  label: "All groups",
                }
              }
              onSelect={(option) => setGroupId(option?.value || "")}
            />
          </div>
          <div className="ctms-list-page__filter-field eplanner-layout__filter-field">
            <label className="form-label small mb-1">Visit</label>
            <Dropdown
              options={[{ value: "", label: "All visits" }, ...visitOptions]}
              selected={
                visitOptions.find((option) => option.value === visitId) || {
                  value: "",
                  label: "All visits",
                }
              }
              onSelect={(option) => setVisitId(option?.value || "")}
            />
          </div>
          {extraFilters}
          </div>
        </section>
        ) : null}
        <div className="eplanner-page__content">{children || <Outlet />}</div>
      </div>
    </EplannerFilterContext.Provider>
  );
}
